import { useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  useTeamStages,
  createStage,
  updateStage,
  deleteStage,
  reorderStages,
  useTeamMembers,
} from '../tareas/api';
import type { Stage } from '../tareas/types';
import { useDialog } from '../ui/Dialogs';

const DEFAULT_COLOR = '#2563eb';

// Etapas del tablero (Configuración → Etapas). A diferencia de las tags, son
// compartidas por todo el espacio: las definen los admins y cada hilo lo mueve
// su asignado. Los miembros sin rol de admin las ven en solo lectura.
export default function TeamStages() {
  const { profile } = useAuth();
  const { alert } = useDialog();
  const { stages, loading, refetch } = useTeamStages();
  const { members } = useTeamMembers();
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const myRole = members.find((m) => m.id === profile?.id)?.role;
  const canManage = myRole === 'owner' || myRole === 'admin';

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError('');
    try {
      const position = stages.reduce((max, s) => Math.max(max, s.position), -1) + 1;
      await createStage({ nombre: newName, color: newColor, position });
      setNewName('');
      setNewColor(DEFAULT_COLOR);
      await refetch();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setCreating(false);
    }
  };

  const move = async (index: number, delta: -1 | 1) => {
    const ids = stages.map((s) => s.id);
    const j = index + delta;
    if (j < 0 || j >= ids.length) return;
    const [moved] = ids.splice(index, 1);
    if (!moved) return;
    ids.splice(j, 0, moved);
    try {
      await reorderStages(ids);
      await refetch();
    } catch (err) {
      alert({ title: 'No se pudo reordenar', message: friendlyError(err) });
    }
  };

  return (
    <>
      <p style={styles.intro}>
        Las etapas son las columnas del tablero de este espacio y dividen los hilos
        «En curso» (p. ej. Pendiente, En progreso, Esperando a tercero). Son comunes a
        todo el equipo; cada hilo lo mueve de etapa su asignado. «Resuelto» y
        «Terminada» son columnas fijas y no se configuran aquí.
      </p>

      {canManage && (
        <section style={styles.card}>
          <h2 style={styles.cardTitle}>Crear nueva etapa</h2>
          <form onSubmit={handleCreate} style={styles.createForm}>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nombre de la etapa (ej. En progreso)"
              maxLength={40}
              required
              style={styles.input}
            />
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              style={styles.colorInput}
              title="Color"
            />
            <button type="submit" disabled={creating} style={styles.btnPrimary}>
              {creating ? 'Creando…' : 'Crear etapa'}
            </button>
          </form>
          {error && <p style={styles.error}>{error}</p>}
        </section>
      )}

      <section style={styles.card}>
        <h2 style={styles.cardTitle}>Etapas del tablero ({stages.length})</h2>
        {loading ? (
          <p style={styles.empty}>Cargando…</p>
        ) : stages.length === 0 ? (
          <p style={styles.empty}>
            Aún no hay etapas: el tablero muestra En curso, Resuelto y Terminada.
            {!canManage && ' Solo los administradores del espacio pueden crearlas.'}
          </p>
        ) : (
          <div style={styles.list}>
            {stages.map((s, i) => (
              <StageRow
                key={s.id}
                stage={s}
                canManage={canManage}
                isFirst={i === 0}
                isLast={i === stages.length - 1}
                onMove={(delta) => move(i, delta)}
                onSaved={refetch}
              />
            ))}
          </div>
        )}
        {!canManage && stages.length > 0 && (
          <p style={{ ...styles.empty, marginTop: 14 }}>
            Solo los administradores del espacio pueden modificar las etapas.
          </p>
        )}
      </section>
    </>
  );
}

// El índice único (team_id, nombre) devuelve un error críptico de Postgres.
function friendlyError(err: unknown): string {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  if (code === '23505') return 'Ya existe una etapa con ese nombre.';
  const message = (err as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' ? message : String(err);
}

interface StageRowProps {
  stage: Stage;
  canManage: boolean;
  isFirst: boolean;
  isLast: boolean;
  onMove: (delta: -1 | 1) => void;
  onSaved: () => Promise<void>;
}

function StageRow({ stage, canManage, isFirst, isLast, onMove, onSaved }: StageRowProps) {
  const { confirm, alert } = useDialog();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(stage.nombre);
  const [color, setColor] = useState(stage.color);
  const [busy, setBusy] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await updateStage(stage.id, { nombre: name, color });
      setEditing(false);
      await onSaved();
    } catch (err) {
      alert({ title: 'No se pudo guardar', message: friendlyError(err) });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Borrar etapa',
      message: `¿Seguro que quieres borrar la etapa "${stage.nombre}"? Los hilos que estén en ella pasarán a «Sin etapa».`,
      confirmLabel: 'Borrar',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteStage(stage.id);
      await onSaved();
    } catch (err) {
      alert({ title: 'No se pudo borrar', message: friendlyError(err) });
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div style={styles.row}>
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={styles.colorInput} />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={40}
          style={{ ...styles.input, flex: 1 }}
        />
        <button type="button" onClick={handleSave} disabled={busy} style={styles.btnPrimarySmall}>
          Guardar
        </button>
        <button
          type="button"
          onClick={() => { setName(stage.nombre); setColor(stage.color); setEditing(false); }}
          style={styles.btnGhost}
        >
          Cancelar
        </button>
      </div>
    );
  }

  return (
    <div style={styles.row}>
      <span style={{ ...styles.dot, background: stage.color }} />
      <span style={{ flex: 1, color: '#0f172a', fontWeight: 600 }}>{stage.nombre}</span>
      {canManage && (
        <>
          <button type="button" onClick={() => onMove(-1)} disabled={isFirst} title="Mover antes" style={{ ...styles.btnArrow, opacity: isFirst ? 0.35 : 1 }}>↑</button>
          <button type="button" onClick={() => onMove(1)} disabled={isLast} title="Mover después" style={{ ...styles.btnArrow, opacity: isLast ? 0.35 : 1 }}>↓</button>
          <button type="button" onClick={() => setEditing(true)} style={styles.btnGhost}>Editar</button>
          <button type="button" onClick={handleDelete} disabled={busy} style={styles.btnDanger}>Borrar</button>
        </>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  intro: { color: '#64748b', fontSize: 14, margin: '0 0 16px', lineHeight: 1.55 },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22, marginBottom: 18 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 16px' },
  createForm: { display: 'flex', gap: 10, alignItems: 'center' },
  input: { border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', background: '#fff', flex: 1 },
  colorInput: { width: 40, height: 40, border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' },
  btnPrimary: { background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnPrimarySmall: { background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#475569', fontSize: 13, fontWeight: 600 },
  btnArrow: { background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', color: '#475569', fontSize: 14, fontWeight: 600, padding: 0 },
  btnDanger: { background: 'transparent', border: '1px solid #fecaca', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#dc2626', fontSize: 13, fontWeight: 600 },
  list: { display: 'flex', flexDirection: 'column', gap: 4 },
  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: '1px solid #f1f5f9' },
  dot: { width: 10, height: 10, minWidth: 10, borderRadius: 5 },
  empty: { color: '#94a3b8', fontSize: 14, margin: 0 },
  error: { color: '#dc2626', fontSize: 13, marginTop: 12, marginBottom: 0 },
};
